'use client';

import dynamic from 'next/dynamic';

import {
  DASHED_TRAIL_DASH,
  DASHED_TRAIL_GAP,
  DASHED_TRAIL_OPACITY,
  DASHED_TRAIL_STROKE_WIDTH,
} from '@/constants/dashedTrailReveal';
import { useDeferredMount } from '@/hooks/useDeferredMount';

import styles from './DashedTrailReveal.module.css';

import type { DashedTrailRevealMotionProps } from './DashedTrailRevealMotion';

const DashedTrailRevealMotionDynamic = dynamic(
  () =>
    import('./DashedTrailRevealMotion').then((mod) => mod.DashedTrailRevealMotion),
  { ssr: false },
);

export type DashedTrailRevealProps = Omit<DashedTrailRevealMotionProps, 'staticPath'>;

function StaticDashedTrailSvg({
  path,
  viewBox,
  className,
  style,
  preserveAspectRatio = 'xMidYMid slice',
  extraClassName,
}: {
  path: string;
  viewBox: string;
  className?: string;
  style?: React.CSSProperties;
  preserveAspectRatio?: string;
  extraClassName?: string;
}) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      preserveAspectRatio={preserveAspectRatio}
      aria-hidden
      className={[className, extraClassName].filter(Boolean).join(' ')}
      style={style}
    >
      <path
        d={path}
        stroke="currentColor"
        strokeWidth={DASHED_TRAIL_STROKE_WIDTH}
        strokeMiterlimit={10}
        strokeLinejoin="round"
        strokeDasharray={`${DASHED_TRAIL_DASH} ${DASHED_TRAIL_GAP}`}
        opacity={DASHED_TRAIL_OPACITY}
        fill="none"
      />
    </svg>
  );
}

/**
 * Grey/black dashed migration trail. Motion stagger is deferred until near
 * the viewport or idle. A CSS `prefers-reduced-motion` twin paints the settled
 * dashed path immediately (no empty-frame flash under visual-diff).
 */
export function DashedTrailReveal({
  path,
  viewBox,
  viewBoxWidth,
  viewBoxHeight,
  className,
  style,
  preserveAspectRatio = 'xMidYMid slice',
  readySelector,
}: DashedTrailRevealProps) {
  const { ref, ready } = useDeferredMount<HTMLSpanElement>();

  return (
    <span ref={ref} aria-hidden className={styles.slot}>
      <StaticDashedTrailSvg
        path={path}
        viewBox={viewBox}
        className={className}
        style={style}
        preserveAspectRatio={preserveAspectRatio}
        extraClassName={styles.reducedStatic}
      />
      <span className={styles.motionSlot}>
        {ready ? (
          <DashedTrailRevealMotionDynamic
            path={path}
            viewBox={viewBox}
            viewBoxWidth={viewBoxWidth}
            viewBoxHeight={viewBoxHeight}
            className={className}
            style={style}
            preserveAspectRatio={preserveAspectRatio}
            readySelector={readySelector}
          />
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox={viewBox}
            preserveAspectRatio={preserveAspectRatio}
            aria-hidden
            className={className}
            style={style}
          />
        )}
      </span>
    </span>
  );
}
